import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import {
  CLUB_LIFE_CLIENT_COMPOSER_CATEGORIES,
  CLUB_LIFE_ADMIN_COMPOSER_CATEGORIES,
} from "../../../constants/clubLifeCategories";
import { givenNameFromFullName, personDisplayName } from "../../../utils/clientAppUtils";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { useDemoPortalOptional } from "../../../demoPortal/DemoPortalContext";
import s from "./clientApp.module.css";
import { Avatar, Chip, ChipGroup, useToast } from "../../ui";

const CLIENT_COMPOSER_CATS = CLUB_LIFE_CLIENT_COMPOSER_CATEGORIES;

const ADMIN_COMPOSER_CATS = CLUB_LIFE_ADMIN_COMPOSER_CATEGORIES;

const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_PHOTOS = 10;

function newPhotoKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function PhotoIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7Z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="m8 14 2.5-2.5a1.5 1.5 0 0 1 2.12 0L16 14"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="9" cy="9" r="1.25" fill="currentColor" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ClubLifeComposer({
  onPublished,
  compact = false,
  embedded = false,
  variant = "client",
  wallMode = false,
  staffWallMode = false,
  /** Чья стена (для родителя — id ребёнка) */
  wallAthleteId = null,
}) {
  const toast = useToast();
  const demo = useDemoPortalOptional();
  const { profile } = useSelector((state) => state.auth);
  const fileInputRef = useRef(null);
  const isAdmin = variant === "admin";
  const composerCats = isAdmin ? ADMIN_COMPOSER_CATS : CLIENT_COMPOSER_CATS;

  const [open, setOpen] = useState(!compact);
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("event");
  const [title, setTitle] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [shareInClubFeed, setShareInClubFeed] = useState(false);
  const [busy, setBusy] = useState(false);

  const name = personDisplayName(profile?.full_name) || "Вы";
  const firstName = givenNameFromFullName(profile?.full_name);
  const photoUploading = photos.some((p) => p.uploading);
  const uploadedPhotos = photos.filter((p) => p.url);
  const canPublish = Boolean(body.trim()) && !busy && !photoUploading;

  const clearPhotos = () => {
    setPhotos((prev) => {
      prev.forEach((p) => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
      return [];
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (key) => {
    setPhotos((prev) => {
      const item = prev.find((p) => p.key === key);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((p) => p.key !== key);
    });
  };

  const close = () => {
    setOpen(false);
    setTitle("");
    setIsPinned(false);
    setShareInClubFeed(false);
    clearPhotos();
  };

  const pickPhoto = () => {
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Не более ${MAX_PHOTOS} фото`);
      return;
    }
    fileInputRef.current?.click();
  };

  const uploadOnePhoto = async (file) => {
    const key = newPhotoKey();
    const preview = URL.createObjectURL(file);
    setPhotos((prev) => [...prev, { key, preview, url: "", uploading: true }]);

    try {
      if (demo?.isDemo) {
        // Keep object URL locally — never upload in demo sandbox
        setPhotos((prev) =>
          prev.map((p) =>
            p.key === key ? { ...p, url: preview, uploading: false } : p,
          ),
        );
        return;
      }
      const upload =
        isAdmin || staffWallMode
          ? clinicAPI.uploadAdminClubLifePhoto
          : clinicAPI.uploadClubLifePhoto;
      const r = await upload(file);
      setPhotos((prev) =>
        prev.map((p) =>
          p.key === key ? { ...p, url: r.url || "", uploading: false } : p,
        ),
      );
    } catch (err) {
      URL.revokeObjectURL(preview);
      setPhotos((prev) => prev.filter((p) => p.key !== key));
      toast.error(err.response?.data?.error || "Не удалось загрузить фото");
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    const slotsLeft = MAX_PHOTOS - photos.length;
    if (slotsLeft <= 0) {
      toast.error(`Не более ${MAX_PHOTOS} фото`);
      return;
    }

    const batch = [];
    for (const file of files) {
      if (batch.length >= slotsLeft) break;
      if (!PHOTO_TYPES.includes(file.type)) {
        toast.error(`${file.name}: допустимы JPEG, PNG, WebP, GIF`);
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        toast.error(`${file.name}: до 10 МБ`);
        continue;
      }
      batch.push(file);
    }

    if (files.length > slotsLeft && batch.length > 0) {
      toast.error(`Добавлено ${batch.length} из ${files.length} (лимит ${MAX_PHOTOS})`);
    }

    for (const file of batch) {
      await uploadOnePhoto(file);
    }
  };

  const publish = async () => {
    const text = body.trim();
    if (!text) {
      toast.error("Напишите текст");
      return;
    }
    if (photoUploading) return;

    setBusy(true);
    try {
      const media = uploadedPhotos.map((p) => ({ url: p.url, type: "image" }));
      if (demo?.isDemo) {
        demo.publishPost({
          body: text,
          category: isAdmin ? category : category,
          media,
        });
        toast.success("Опубликовано только в демо");
        setBody("");
        setTitle("");
        setIsPinned(false);
        setShareInClubFeed(false);
        clearPhotos();
        if (compact) setOpen(false);
        onPublished?.();
        return;
      }
      if (isAdmin) {
        await clinicAPI.createClubLifePost({
          body: text,
          category,
          title: title.trim() || null,
          media,
          is_pinned: isPinned,
        });
      } else if (wallMode && staffWallMode) {
        await clinicAPI.createStaffWallPost({
          body: text,
          media,
          share_in_club_feed: shareInClubFeed,
        });
      } else if (wallMode) {
        const wallParams = wallAthleteId
          ? { athlete_id: wallAthleteId }
          : {};
        await clinicAPI.createWallPost(
          {
            body: text,
            media,
            share_in_club_feed: shareInClubFeed,
          },
          wallParams,
        );
      } else {
        await clinicAPI.createMyClubLifePost({
          body: text,
          category,
          media,
        });
      }
      toast.success("Опубликовано");
      setBody("");
      setTitle("");
      setIsPinned(false);
      setShareInClubFeed(false);
      clearPhotos();
      if (compact) setOpen(false);
      onPublished?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось опубликовать");
    } finally {
      setBusy(false);
    }
  };

  if (compact && !open) {
    return (
      <div
        className={`${s.postComposerBar} ${
          embedded ? s.postComposerBarEmbedded : ""
        }`}
      >
        <Avatar name={name} src={mediaUrl(profile?.photo_url)} size="md" />
        <button
          type="button"
          className={s.postComposerPrompt}
          onClick={() => setOpen(true)}
        >
          {firstName
            ? `Что нового, ${firstName}?`
            : wallMode
              ? "Что нового?"
              : "Поделитесь новостью…"}
        </button>
      </div>
    );
  }

  const cardClass = [
    s.postComposerCard,
    s.postComposerCardOpen,
    embedded ? s.postComposerCardEmbedded : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cardClass}>
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_TYPES.join(",")}
        multiple
        className={s.postComposerFileInput}
        onChange={handlePhotoSelect}
      />

      <div className={s.postComposerHead}>
        <div className={s.postComposerUser}>
          <Avatar name={name} src={mediaUrl(profile?.photo_url)} size="md" />
          <span>{name}</span>
        </div>
        {compact ? (
          <button
            type="button"
            className={s.postComposerClose}
            onClick={close}
            aria-label="Закрыть"
          >
            ×
          </button>
        ) : null}
      </div>

      <div className={s.postComposerBody}>
        <textarea
          className={s.postComposerText}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            wallMode ? "Что нового?" : "О чём хотите рассказать клубу?"
          }
          rows={4}
          autoFocus={compact && open}
        />
      </div>

      {isAdmin ? (
        <input
          type="text"
          className={s.postComposerTitle}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Заголовок (необязательно)"
        />
      ) : null}

      {!isAdmin && !wallMode ? (
        <p className={s.postComposerNotice} role="note">
          Публикация видна всем участникам клуба в разделе «Клуб».
        </p>
      ) : null}

      {wallMode && !isAdmin ? (
        <div className={s.postComposerVisibility}>
          <p className={s.postComposerVisibilityLabel}>Кто увидит запись</p>
          <div className={s.postComposerVisibilityOptions}>
            <button
              type="button"
              className={
                !shareInClubFeed
                  ? s.postComposerVisibilityOn
                  : s.postComposerVisibilityBtn
              }
              onClick={() => setShareInClubFeed(false)}
            >
              <span className={s.postComposerVisibilityTitle}>
                Только на моей стене
              </span>
              <span className={s.postComposerVisibilityHint}>
                Видно в профиле
              </span>
            </button>
            <button
              type="button"
              className={
                shareInClubFeed
                  ? s.postComposerVisibilityOn
                  : s.postComposerVisibilityBtn
              }
              onClick={() => setShareInClubFeed(true)}
            >
              <span className={s.postComposerVisibilityTitle}>
                Стена и лента клуба
              </span>
              <span className={s.postComposerVisibilityHint}>
                В профиле и в разделе «Клуб»
              </span>
            </button>
          </div>
        </div>
      ) : null}

      {photos.length > 0 ? (
        <div className={s.postComposerPreviewGrid}>
          {photos.map((p) => (
            <div key={p.key} className={s.postComposerPreview}>
              <img src={p.preview} alt="" className={s.postComposerPreviewImg} />
              {p.uploading ? (
                <span className={s.postComposerPreviewBusy}>…</span>
              ) : null}
              <button
                type="button"
                className={s.postComposerPreviewRemove}
                onClick={() => removePhoto(p.key)}
                disabled={p.uploading}
                aria-label="Убрать фото"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {!wallMode ? (
        <ChipGroup className={s.postComposerChips} label="Тип публикации">
          {composerCats.map((c) => (
            <Chip
              key={c.id}
              selected={category === c.id}
              onClick={() => setCategory(c.id)}
            >
              <span className={s.postComposerChipIcon} aria-hidden>
                {c.icon}
              </span>
              {c.label}
            </Chip>
          ))}
        </ChipGroup>
      ) : null}

      <div className={s.postComposerActions}>
        <div className={s.postComposerActionsLeft}>
          <button
            type="button"
            className={photos.length > 0 ? s.postComposerToolOn : s.postComposerTool}
            onClick={pickPhoto}
            disabled={photoUploading || photos.length >= MAX_PHOTOS}
            aria-label="Добавить фото"
          >
            <PhotoIcon />
          </button>
          {isAdmin ? (
            <label className={s.postComposerPin}>
              <input
                type="checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
              />
              <span>Закрепить</span>
            </label>
          ) : null}
        </div>
        <div className={s.postComposerActionsRight}>
          {compact ? (
            <button type="button" className={s.postComposerCancel} onClick={close}>
              Отмена
            </button>
          ) : null}
          <button
            type="button"
            className={canPublish ? s.postComposerPublish : s.postComposerPublishOff}
            disabled={!canPublish}
            onClick={publish}
          >
            {busy ? "…" : (
              <>
                <span>Опубликовать</span>
                <SendIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
